import { createSlice, current } from "@reduxjs/toolkit";
import { getMonthsInRange } from "../common/utils/helperFunctions";

const initialState = {
    activeKey: "0",
    detectedKey: null,
    innerActiveKey: null,
    stacCollection: {},
    loadingSearchResults: false,
    loadingObjectResults: false,
    loadingSimilarResults: false,
    searchResults: {},
    similarResults: {},
    detectedObject: {},
    selectedFeature: {},
    selectedDectedObject: {},
    highlightedMonthRange: [],
    stacCollectionsList: [],
    searchedMultiResults: [],
    selectedSimilarImageIds: [],
    overlayLayer: false,
    selectedOverlayKey: null,
    detectedObjectModal: false,
    hoveredResultIndex: null,
};

const getHighlightedMonthRange = (searchResults) => {
    const features = searchResults?.features || [];
    const totalFeatures = features.length;
  
    if (totalFeatures > 0) {
      const startFeatureDate =
        features[totalFeatures - 1]?.properties?.datetime;
      const lastFeatureDate =
        features[0]?.properties?.datetime;
  
      return getMonthsInRange(startFeatureDate, lastFeatureDate)
        .map(item => item.monthName);
    }
  
    return [];
  };
  

const saveSelectedFeature = (searchResults, selectedFeature) => {

    // need to make it consistent for both (stac and feature collection)
    let selectedFeatureData = [];
    selectedFeatureData = searchResults.features.filter((feature) => {
        return feature.properties['geodata:item_id'] === selectedFeature.properties['geodata:item_id'];
    });

    if (selectedFeatureData.length > 0) {
        return selectedFeatureData[0];
    };

    return {};

};

const searchSlice = createSlice({
    name: 'searchSlice',
    initialState,
    reducers: {
        setActiveKey: (state, action) => {
            state.activeKey = action.payload;
        },
        setInnerActiveKey: (state, action) => {
            state.innerActiveKey = action.payload;
        },
        setDetectedKey: (state, action) => {
            state.detectedKey = action.payload;
        },
        getStacDataRequest: (state) => {
            state.loading = true;
        },
        setFilteredStacCollection: (state, action) => {
            state.stacCollection = action.payload;
            state.highlightedMonthRange = getHighlightedMonthRange(action.payload);
            state.loading = false;
        },
        getStacDataFailure: (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.stacCollection = {};
        },
        searchStacDataRequest: (state) => {
            state.loadingSearchResults = true;
            state.stacCollection = {};
            state.searchResults = {};
            state.selectedFeature = {};
            state.similarResults = {};
        },
        searchStacDataSuccess: (state, action) => {
            state.searchResults = action.payload;
            state.highlightedMonthRange = getHighlightedMonthRange(action.payload);
            state.loadingSearchResults = false;
        },
        searchStacDataFailure: (state, action) => {
            state.loadingSearchResults = false;
            state.error = action.payload;
            state.searchResults = {};
        },
        // Order items arrive one GET per item (index-server has no `ids` filter -
        // see searchOrderItem in stac.service.jsx), so accumulate into searchResults
        // the same way getMultiStacDataSuccess accumulates into searchedMultiResults.
        searchOrderItemSuccess: (state, action) => {
            if (!state.searchResults?.features) {
                state.searchResults = { type: 'FeatureCollection', features: [] };
            }
            state.searchResults.features.push(action.payload);
            state.loadingSearchResults = false;
        },
        setSelectedFeature: (state, action) => {
            state.selectedFeature = saveSelectedFeature(state.searchResults, action.payload);
        },
        resetSelectedFeature: (state) => {
            state.selectedFeature = {};
        },
        resetSearchCollections: (state) => {
            state.searchResults = {};
            state.stacCollection = {};
        },
        resetSearchSlice: () => {
            return initialState;
        },
        getStacCollectionsRequest: (state) => {
            state.stacCollectionsList = {};
        },
        getStacCollectionsSuccess: (state, action) => {
            state.stacCollectionsList = action.payload.collections;
        },
        getStacCollectionsFailure: (state, action) => {
            state.error = action.payload;
        },
        getMultiStacDataRequest: (state) => {
            state.searchedMultiResults = [];
        },
        getMultiStacDataSuccess: (state, action) => {
            state.searchedMultiResults.push(action.payload);
          },
        getMultiStacDataFailure: (state, action) => {
            state.error = action.payload;
        },
        resetMultiStacData: (state) => {
            state.searchedMultiResults = [];
        },
        getMultiStacHighlightData: (state, action) => {
            state.highlightedMonthRange = getHighlightedMonthRange(action.payload);
        },
        searchObjectDetectionDataRequest: (state) => {
            state.loadingObjectResults = true;
            state.detectedObject = {};
            state.selectedDectedObject = {};
        },
        searchObjectDetectionDataSuccess: (state, action) => {
            state.loadingObjectResults = false;
            state.detectedObject = action.payload;
        },
        searchObjectDetectionDataFailure: (state, action) => {
            state.loadingObjectResults = false;
            state.error = action.payload;
            state.detectedObject = {};
        },
        enableOverlayLayer: (state) => {
            state.overlayLayer = true;
        },
        disableOverlayLayer: (state) => {
            state.overlayLayer = false;
            state.detectedObject = {};
        },
        setSelectedOverlayKey: (state, action) => {
            state.selectedOverlayKey = action.payload;
        },
        resetSelectedOverlayKey: (state) => {
            state.selectedOverlayKey = null;
        },
        setSelectedDetectedObject: (state, action) => {
            state.selectedDectedObject = saveSelectedFeature(state.detectedObject,action.payload);
        },
        resetSelectedDetectedObject: (state) => {
            state.selectedDectedObject = {};
        },
        setDetectedObjectModal: (state) => {
            state.detectedObjectModal = true;
        },
        resetDetectedObjectModal: (state) => {
            state.detectedObjectModal = false;
        },
        searchSimilarStacDataRequest: (state) => {
            state.loadingSimilarResults = true;
            state.similarResults = {};
        },
        searchSimilarStacDataSuccess: (state, action) => {
            state.similarResults = action.payload;
            state.loadingSimilarResults = false;
        },
        searchSimilarStacDataFailure: (state, action) => {
            state.loadingSearchResults = false;
            state.error = action.payload;
            state.similarResults = {};
        },
        clearSimilarResults: (state) => {
            state.similarResults = {};
        },
        setSelectedFeatureIds(state, action) {
            state.selectedSimilarImageIds = action.payload;
        },
        clearSelectedFeatureIds(state) {
            state.selectedSimilarImageIds = [];
        },
        setHoveredResult(state, action) {
            state.hoveredResultIndex = action.payload;
        },
    },
});

export default searchSlice.reducer;

export const {
    setActiveKey,
    setDetectedKey,
    setInnerActiveKey,
    getStacDataRequest,
    setFilteredStacCollection,
    getStacDataFailure,
    searchStacDataRequest,
    searchStacDataSuccess,
    searchStacDataFailure,
    searchOrderItemSuccess,
    setSelectedFeature,
    resetSelectedFeature,
    resetSearchCollections,
    resetSearchSlice,
    getStacCollectionsRequest,
    getStacCollectionsSuccess,
    getStacCollectionsFailure,
    getMultiStacDataRequest,
    getMultiStacDataSuccess,
    getMultiStacDataFailure,
    resetMultiStacData,
    getMultiStacHighlightData,
    searchObjectDetectionDataRequest,
    searchObjectDetectionDataSuccess,
    searchObjectDetectionDataFailure,
    enableOverlayLayer,
    disableOverlayLayer,
    setSelectedOverlayKey,
    resetSelectedOverlayKey,
    setSelectedDetectedObject,
    resetSelectedDetectedObject,
    setDetectedObjectModal,
    resetDetectedObjectModal,
    searchSimilarStacDataRequest,
    searchSimilarStacDataSuccess,
    searchSimilarStacDataFailure,
    clearSimilarResults,
    setSelectedFeatureIds,
    clearSelectedFeatureIds,
    setHoveredResult
} = searchSlice.actions;
